<template>
  <v-container fluid class="pa-2 pt-0">
    <v-row dense class="align-center mb-2">
      <v-col cols="12" sm="6" class="d-flex align-center pb-0">
        <h3 class="mb-0">{{ divisionName }} Organogram</h3>
      </v-col>
      <v-col cols="12" sm="6" class="d-flex justify-end align-center pb-0">
        <div style="width: 220px;">
          <v-select
            v-model="selectedFinancialYear"
            :items="yearList"
            label="Financial Year"
            dense
            outlined
            rounded
            hide-details
            class="fy-select-pill"
            @change="fetchOrganogram"
          ></v-select>
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="pt-0">
        <v-card v-if="!loading">
          <OrganogramFlow
            :employees="employees"
            :layout="layout"
            :isAdmin="true"
            :divisionName="divisionName"
            :financialYear="selectedFinancialYear"
            :isPastYear="isPastYear"
            :hasSavedLayout="hasSavedLayout"
            @save-layout="onSaveLayout"
            @init-past-layout="fetchEmployeesForPastYear"
          />
        </v-card>
        <div v-else class="text-center py-5">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import OrganogramFlow from '@/components/OrganogramFlow.vue';

export default {
  name: 'AdminTeamOrganogram',
  components: {
    OrganogramFlow
  },
  data() {
    return {
      divisionId: null,
      divisionName: 'Division',
      employees: [],
      layout: null,
      loading: true,
      selectedFinancialYear: null,
      isPastYear: false,
      hasSavedLayout: true,
      yearList: []
    };
  },
  async created() {
    this.divisionId = this.$route.params.id;
    this.initFinancialYear();
    await this.fetchFinancialYears();
    await this.fetchOrganogram();
  },
  methods: {
    initFinancialYear() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      this.selectedFinancialYear = month < 3 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
    },
    async fetchFinancialYears() {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/financialyear/getlist', {
          headers: { 'x-auth-token': token, token }
        });
        if (res.data && res.data.status && res.data.data) {
          this.yearList = res.data.data;
          if (!this.yearList.includes(this.selectedFinancialYear)) {
            this.yearList.unshift(this.selectedFinancialYear);
          }
        } else {
          this.yearList = [
            '2022-2023', '2023-2024', '2024-2025', '2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030'
          ];
        }
      } catch (e) {
        console.error("Error fetching financial years:", e);
        this.yearList = [
          '2022-2023', '2023-2024', '2024-2025', '2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030'
        ];
      }
    },
    async fetchOrganogram() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/division-organogram/${this.divisionId}`, {
          params: { financialYear: this.selectedFinancialYear },
          headers: { 'x-auth-token': token }
        });
        if (res.data && res.data.status) {
          this.employees = res.data.data.employees || [];
          this.layout = res.data.data.layout || null;
          this.isPastYear = !!res.data.data.isPastYear;
          this.hasSavedLayout = !!res.data.data.hasSavedLayout;
          if (res.data.data.divisionName) {
            this.divisionName = res.data.data.divisionName;
          }
        }
      } catch (e) {
        console.error("Error fetching organogram:", e);
      } finally {
        this.loading = false;
      }
    },
    async fetchEmployeesForPastYear() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/division-organogram/${this.divisionId}`, {
          params: { financialYear: this.selectedFinancialYear, forceLoadEmployees: true },
          headers: { 'x-auth-token': token }
        });
        if (res.data && res.data.status) {
          this.employees = res.data.data.employees || [];
          this.layout = { nodeStyles: [], customNodes: [], customEdges: [], deletedNodes: [], financialYear: this.selectedFinancialYear };
          this.hasSavedLayout = true;
        }
      } catch (e) {
        console.error("Error initializing layout:", e);
      } finally {
        this.loading = false;
      }
    },
    async onSaveLayout(layoutData) {
      try {
        const token = localStorage.getItem('token');
        
        const res = await axios.put('/division-organogram/node-style', {
          divisionId: this.divisionId,
          financialYear: this.selectedFinancialYear,
          nodeStyles: layoutData.nodeStyles,
          customNodes: layoutData.customNodes,
          customEdges: layoutData.customEdges,
          deletedNodes: layoutData.deletedNodes
        }, { headers: { 'x-auth-token': token } });
        
        if (res.data && res.data.status && res.data.data) {
          this.layout = res.data.data;
        } else {
          this.layout = {
            nodeStyles: layoutData.nodeStyles,
            customNodes: layoutData.customNodes,
            customEdges: layoutData.customEdges,
            deletedNodes: layoutData.deletedNodes,
            financialYear: this.selectedFinancialYear
          };
        }
        this.hasSavedLayout = true;
        alert(`Layout for Financial Year ${this.selectedFinancialYear} saved successfully`);
      } catch (e) {
        console.error("Error saving layout:", e);
        alert("Failed to save layout");
      }
    }
  }
};
</script>

<style scoped>
.fy-select-pill >>> .v-input__control > .v-input__slot,
.fy-select-pill >>> .v-input__control > .v-input__slot fieldset,
.fy-select-pill >>> fieldset {
  border-radius: 28px !important;
}
</style>
